
import { useState } from "react";
import { base44 } from "@/api/base44Client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Upload, CheckCircle, AlertCircle, Loader2, Database } from "lucide-react";
import { format } from "date-fns";

export default function ImportHistoricalData() {
  const [eaRosterData, setEaRosterData] = useState("");
  const [asdRosterData, setAsdRosterData] = useState("");
  const [consequenceData, setConsequenceData] = useState("");
  const [processing, setProcessing] = useState(false);
  const [message, setMessage] = useState(null);
  const [progress, setProgress] = useState("");

  const queryClient = useQueryClient();

  const { data: allUsers = [] } = useQuery({
    queryKey: ['allUsers'],
    queryFn: () => base44.entities.User.list()
  });

  const { data: consequenceLevels = [] } = useQuery({
    queryKey: ['consequenceLevels'],
    queryFn: () => base44.entities.ConsequenceLevel.list('sort_order')
  });

  const aps = allUsers.filter(u => u.role === 'user' && u.user_type === 'assistant_principal');

  const processEARosterMutation = useMutation({
    mutationFn: async () => {
      if (!eaRosterData.trim()) throw new Error("No EA roster data provided");

      const lines = eaRosterData.trim().split('\n').map(l => l.trim()).filter(l => l);
      if (lines.length === 0) throw new Error("No data found");

      const headerLine = lines[0];
      const headers = headerLine.split('\t').map(h => h.trim().toLowerCase());

      const colMap = {
        campus: headers.findIndex(h => h.includes('campus')),
        studentLast: headers.findIndex(h => h.includes('student last')),
        studentFirst: headers.findIndex(h => h.includes('student first')),
        otherId: headers.findIndex(h => h.includes('other id')),
        studentId: headers.findIndex(h => h === 'student id'),
        methodOfInstruction: headers.findIndex(h => h.includes('method of instruction')),
        gradeLevel: headers.findIndex(h => h.includes('grade level')),
        course: headers.findIndex(h => h === 'course'),
        section: headers.findIndex(h => h === 'section'),
        courseShortDesc: headers.findIndex(h => h.includes('course short description')),
        teacher: headers.findIndex(h => h === 'teacher'),
        teacherEmail: headers.findIndex(h => h.includes('teacher email') || h === 'teacher e-mail'),
        assignment: headers.findIndex(h => h === 'assignment' && !h.includes('grade')),
        assignmentGrade: headers.findIndex(h => h.includes('assignment grade')),
        athleticGrade: headers.findIndex(h => h.includes('athletic grade')),
        comments: headers.findIndex(h => h === 'comments'),
        date: headers.findIndex(h => h === 'date'),
        time: headers.findIndex(h => h === 'time'),
        f1g1Phone: headers.findIndex(h => h.includes('f1g1 parent phone')),
        f1g2Phone: headers.findIndex(h => h.includes('f1g2 parent phone')),
        studentEmail: headers.findIndex(h => h.includes('student email')),
        f1g1Email: headers.findIndex(h => h.includes('f1g1 parent email')),
        f2g1Phone: headers.findIndex(h => h.includes('f2g1 parent phone')),
        f2g1Email: headers.findIndex(h => h.includes('f2g1 parent email')),
      };

      if (colMap.otherId === -1 || colMap.studentLast === -1 || colMap.studentFirst === -1 || colMap.teacher === -1) {
        throw new Error("Missing required columns (Other ID, Student Last Name, Student First Name, or Teacher)");
      }

      setProgress("Loading existing entries...");
      const existingEntries = await base44.entities.RosterEntry.list();

      setProgress("Processing EA roster entries...");

      const entriesToCreate = [];
      const duplicateIds = [];
      let skippedCount = 0;

      for (let i = 1; i < lines.length; i++) {
        const line = lines[i];
        const cols = line.split('\t').map(c => c.trim());

        const otherId = cols[colMap.otherId];
        const studentLast = cols[colMap.studentLast];
        const studentFirst = cols[colMap.studentFirst];
        const teacher = cols[colMap.teacher];
        const teacherEmail = colMap.teacherEmail !== -1 ? cols[colMap.teacherEmail] : '';
        const courseShortDesc = colMap.courseShortDesc !== -1 ? cols[colMap.courseShortDesc] : '';

        if (!otherId || !studentLast || !studentFirst || !teacher) {
          skippedCount++;
          continue;
        }

        // Find duplicates to remove
        const duplicates = existingEntries.filter(e =>
          e.other_id === otherId &&
          e.teacher === teacher &&
          e.course_short_description === courseShortDesc &&
          e.status === 'active'
        );

        duplicates.forEach(dup => duplicateIds.push(dup.id));

        // Prepare entry for bulk create
        entriesToCreate.push({
          attendance_status: 'pending',
          campus: colMap.campus !== -1 ? cols[colMap.campus] : '',
          student_last_name: studentLast,
          student_first_name: studentFirst,
          other_id: otherId,
          student_id: colMap.studentId !== -1 ? cols[colMap.studentId] : '',
          method_of_instruction: colMap.methodOfInstruction !== -1 ? cols[colMap.methodOfInstruction] : '',
          grade_level: colMap.gradeLevel !== -1 ? cols[colMap.gradeLevel] : '',
          course: colMap.course !== -1 ? cols[colMap.course] : '',
          section: colMap.section !== -1 ? cols[colMap.section] : '',
          course_short_description: courseShortDesc,
          teacher: teacher,
          teacher_email: teacherEmail,
          assignment: colMap.assignment !== -1 ? cols[colMap.assignment] : '',
          assignment_grade: colMap.assignmentGrade !== -1 ? cols[colMap.assignmentGrade] : '',
          athletic_grade: colMap.athleticGrade !== -1 ? cols[colMap.athleticGrade] : '',
          comments: colMap.comments !== -1 ? cols[colMap.comments] : '',
          date: colMap.date !== -1 ? cols[colMap.date] : '',
          time: colMap.time !== -1 ? cols[colMap.time] : '',
          f1g1_parent_phone: colMap.f1g1Phone !== -1 ? cols[colMap.f1g1Phone] : '',
          f1g2_parent_phone: colMap.f1g2Phone !== -1 ? cols[colMap.f1g2Phone] : '',
          student_email: colMap.studentEmail !== -1 ? cols[colMap.studentEmail] : '',
          f1g1_parent_email: colMap.f1g1Email !== -1 ? cols[colMap.f1g1Email] : '',
          f2g1_parent_phone: colMap.f2g1Phone !== -1 ? cols[colMap.f2g1Phone] : '',
          f2g1_parent_email: colMap.f2g1Email !== -1 ? cols[colMap.f2g1Email] : '',
          report_date: format(new Date(), 'yyyy-MM-dd'),
          status: 'active'
        });
      }

      // Delete duplicates in batches
      if (duplicateIds.length > 0) {
        setProgress(`Removing ${duplicateIds.length} duplicates...`);
        for (let i = 0; i < duplicateIds.length; i += 50) {
          const batch = duplicateIds.slice(i, i + 50);
          await Promise.all(batch.map(id => base44.entities.RosterEntry.delete(id)));
        }
      }

      // Bulk create new entries
      if (entriesToCreate.length > 0) {
        setProgress(`Creating ${entriesToCreate.length} new entries...`);
        await base44.entities.RosterEntry.bulkCreate(entriesToCreate);
      }

      return {
        addedCount: entriesToCreate.length,
        duplicatesRemoved: duplicateIds.length,
        skippedCount
      };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(['rosterEntries']);
      setMessage({
        type: 'success',
        text: `✅ EA Roster: Added ${data.addedCount} entries, removed ${data.duplicatesRemoved} duplicates${data.skippedCount > 0 ? `, skipped ${data.skippedCount}` : ''}.`
      });
      setEaRosterData("");
      setProcessing(false);
      setProgress("");
    },
    onError: (err) => {
      setMessage({ type: 'error', text: err.message || 'Failed to process EA roster' });
      setProcessing(false);
      setProgress("");
    }
  });

  const processASDRosterMutation = useMutation({
    mutationFn: async () => {
      if (!asdRosterData.trim()) throw new Error("No ASD roster data provided");

      const lines = asdRosterData.trim().split('\n').map(l => l.trim()).filter(l => l);
      if (lines.length === 0) throw new Error("No data found");

      const headerLine = lines[0];
      const headers = headerLine.split('\t').map(h => h.trim().toLowerCase());

      const colMap = {
        campus: headers.findIndex(h => h.includes('campus')),
        studentLast: headers.findIndex(h => h.includes('student last')),
        studentFirst: headers.findIndex(h => h.includes('student first')),
        otherId: headers.findIndex(h => h.includes('other id')),
        studentId: headers.findIndex(h => h === 'student id'),
        methodOfInstruction: headers.findIndex(h => h.includes('method of instruction')),
        gradeLevel: headers.findIndex(h => h.includes('grade level')),
        course: headers.findIndex(h => h === 'course'),
        section: headers.findIndex(h => h === 'section'),
        courseShortDesc: headers.findIndex(h => h.includes('course short description')),
        teacher: headers.findIndex(h => h === 'teacher'),
        teacherEmail: headers.findIndex(h => h.includes('teacher email') || h === 'teacher e-mail'),
        assignment: headers.findIndex(h => h === 'assignment' && !h.includes('grade')),
        assignmentGrade: headers.findIndex(h => h.includes('assignment grade')),
        athleticGrade: headers.findIndex(h => h.includes('athletic grade')),
        comments: headers.findIndex(h => h === 'comments'),
        date: headers.findIndex(h => h === 'date'),
        time: headers.findIndex(h => h === 'time'),
        f1g1Phone: headers.findIndex(h => h.includes('f1g1 parent phone')),
        f1g2Phone: headers.findIndex(h => h.includes('f1g2 parent phone')),
        studentEmail: headers.findIndex(h => h.includes('student email')),
        f1g1Email: headers.findIndex(h => h.includes('f1g1 parent email')),
        f2g1Phone: headers.findIndex(h => h.includes('f2g1 parent phone')),
        f2g1Email: headers.findIndex(h => h.includes('f2g1 parent email')),
      };

      if (colMap.otherId === -1 || colMap.studentLast === -1 || colMap.studentFirst === -1 || colMap.teacher === -1) {
        throw new Error("Missing required columns (Other ID, Student Last Name, Student First Name, or Teacher)");
      }

      setProgress("Loading existing entries...");
      const existingEntries = await base44.entities.RosterEntry.list();

      setProgress("Processing ASD roster entries...");

      const entriesToCreate = [];
      const duplicateIds = [];
      let skippedCount = 0;

      for (let i = 1; i < lines.length; i++) {
        const line = lines[i];
        const cols = line.split('\t').map(c => c.trim());

        const otherId = cols[colMap.otherId];
        const studentLast = cols[colMap.studentLast];
        const studentFirst = cols[colMap.studentFirst];
        const teacher = cols[colMap.teacher];
        const teacherEmail = colMap.teacherEmail !== -1 ? cols[colMap.teacherEmail] : '';
        const courseShortDesc = colMap.courseShortDesc !== -1 ? cols[colMap.courseShortDesc] : '';

        if (!otherId || !studentLast || !studentFirst || !teacher) {
          skippedCount++;
          continue;
        }

        // Find duplicates to remove
        const duplicates = existingEntries.filter(e =>
          e.other_id === otherId &&
          e.teacher === teacher &&
          e.course_short_description === courseShortDesc &&
          e.status === 'asd'
        );

        duplicates.forEach(dup => duplicateIds.push(dup.id));

        // Prepare entry for bulk create
        entriesToCreate.push({
          attendance_status: 'pending',
          campus: colMap.campus !== -1 ? cols[colMap.campus] : '',
          student_last_name: studentLast,
          student_first_name: studentFirst,
          other_id: otherId,
          student_id: colMap.studentId !== -1 ? cols[colMap.studentId] : '',
          method_of_instruction: colMap.methodOfInstruction !== -1 ? cols[colMap.methodOfInstruction] : '',
          grade_level: colMap.gradeLevel !== -1 ? cols[colMap.gradeLevel] : '',
          course: colMap.course !== -1 ? cols[colMap.course] : '',
          section: colMap.section !== -1 ? cols[colMap.section] : '',
          course_short_description: courseShortDesc,
          teacher: teacher,
          teacher_email: teacherEmail,
          assignment: colMap.assignment !== -1 ? cols[colMap.assignment] : '',
          assignment_grade: colMap.assignmentGrade !== -1 ? cols[colMap.assignmentGrade] : '',
          athletic_grade: colMap.athleticGrade !== -1 ? cols[colMap.athleticGrade] : '',
          comments: colMap.comments !== -1 ? cols[colMap.comments] : '',
          date: colMap.date !== -1 ? cols[colMap.date] : '',
          time: colMap.time !== -1 ? cols[colMap.time] : '',
          f1g1_parent_phone: colMap.f1g1Phone !== -1 ? cols[colMap.f1g1Phone] : '',
          f1g2_parent_phone: colMap.f1g2Phone !== -1 ? cols[colMap.f1g2Phone] : '',
          student_email: colMap.studentEmail !== -1 ? cols[colMap.studentEmail] : '',
          f1g1_parent_email: colMap.f1g1Email !== -1 ? cols[colMap.f1g1Email] : '',
          f2g1_parent_phone: colMap.f2g1Phone !== -1 ? cols[colMap.f2g1Phone] : '',
          f2g1_parent_email: colMap.f2g1Email !== -1 ? cols[colMap.f2g1Email] : '',
          report_date: format(new Date(), 'yyyy-MM-dd'),
          status: 'asd'
        });
      }

      // Delete duplicates in batches
      if (duplicateIds.length > 0) {
        setProgress(`Removing ${duplicateIds.length} duplicates...`);
        for (let i = 0; i < duplicateIds.length; i += 50) {
          const batch = duplicateIds.slice(i, i + 50);
          await Promise.all(batch.map(id => base44.entities.RosterEntry.delete(id)));
        }
      }

      // Bulk create new entries
      if (entriesToCreate.length > 0) {
        setProgress(`Creating ${entriesToCreate.length} new entries...`);
        await base44.entities.RosterEntry.bulkCreate(entriesToCreate);
      }

      return {
        addedCount: entriesToCreate.length,
        duplicatesRemoved: duplicateIds.length,
        skippedCount
      };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(['rosterEntries']);
      setMessage({
        type: 'success',
        text: `✅ ASD Roster: Added ${data.addedCount} entries, removed ${data.duplicatesRemoved} duplicates${data.skippedCount > 0 ? `, skipped ${data.skippedCount}` : ''}.`
      });
      setAsdRosterData("");
      setProcessing(false);
      setProgress("");
    },
    onError: (err) => {
      setMessage({ type: 'error', text: err.message || 'Failed to process ASD roster' });
      setProcessing(false);
      setProgress("");
    }
  });

  const processConsequencesMutation = useMutation({
    mutationFn: async () => {
      if (!consequenceData.trim()) throw new Error("No consequence data provided");

      const lines = consequenceData.trim().split('\n').map(l => l.trim()).filter(l => l);
      if (lines.length === 0) throw new Error("No data found");

      const headerLine = lines[0];
      const headers = headerLine.split('\t').map(h => h.trim().toLowerCase());

      const colMap = {
        otherId: headers.findIndex(h => h.includes('other id') || h.includes('student id')),
        studentName: headers.findIndex(h => h.includes('student name') || h.includes('name')),
        gradeLevel: headers.findIndex(h => h.includes('grade')),
        refusalCount: headers.findIndex(h => h.includes('refusal') && (h.includes('count') || h.includes('days'))),
        consequenceLevel: headers.findIndex(h => h.includes('consequence') && h.includes('level')),
        makeupOwed: headers.findIndex(h => h.includes('makeup') && h.includes('owed')),
        makeupCompleted: headers.findIndex(h => h.includes('makeup') && h.includes('completed')),
      };

      if (colMap.otherId === -1 || colMap.refusalCount === -1) {
        throw new Error("Missing required columns: Other ID (or Student ID) and Refusal Count are required");
      }

      setProgress("Loading data...");
      const existingTracking = await base44.entities.EAMissTracking.list();
      const rosterEntries = await base44.entities.RosterEntry.list();

      const recordsToCreate = [];
      const recordsToUpdate = [];
      let skippedCount = 0;
      let apAssigned = 0;

      setProgress("Processing consequence tracking data...");

      for (let i = 1; i < lines.length; i++) {
        const line = lines[i];
        const cols = line.split('\t').map(c => c.trim());

        const otherId = cols[colMap.otherId];
        const refusalCount = parseInt(cols[colMap.refusalCount]) || 0;

        if (!otherId || refusalCount === 0) {
          skippedCount++;
          continue;
        }

        // Get student info from roster
        const studentEntry = rosterEntries.find(e => e.other_id === otherId);
        const studentName = colMap.studentName !== -1 && cols[colMap.studentName] ?
          cols[colMap.studentName] :
          studentEntry ? `${studentEntry.student_first_name} ${studentEntry.student_last_name}` : 'Unknown';
        const gradeLevel = colMap.gradeLevel !== -1 && cols[colMap.gradeLevel] ?
          cols[colMap.gradeLevel] :
          studentEntry?.grade_level || '';

        // Determine consequence level with mapping for common variations
        let consequenceLevel = 'warning';
        if (colMap.consequenceLevel !== -1 && cols[colMap.consequenceLevel]) {
          const rawLevel = cols[colMap.consequenceLevel].toLowerCase().trim();
          
          // Map common variations to proper enum values
          const levelMapping = {
            '1st consequence': 'warning',
            '2nd consequence': 'parent_contact',
            '3rd consequence': 'detention',
            '4th consequence': 'iss',
            '5th consequence': 'admin_review',
            'first consequence': 'warning',
            'second consequence': 'parent_contact',
            'third consequence': 'detention',
            'fourth consequence': 'iss',
            'fifth consequence': 'admin_review',
            '1': 'warning',
            '2': 'parent_contact',
            '3': 'detention',
            '4': 'iss',
            '5': 'admin_review',
            'warning': 'warning',
            'parent contact': 'parent_contact',
            'parent_contact': 'parent_contact',
            'detention': 'detention',
            'iss': 'iss',
            'in school suspension': 'iss',
            'admin review': 'admin_review',
            'admin_review': 'admin_review',
            'principal': 'admin_review'
          };
          
          consequenceLevel = levelMapping[rawLevel] || consequenceLevel;
        } else {
          // Auto-calculate based on refusal count
          const levels = consequenceLevels.length > 0 ? consequenceLevels : [
            { level_key: 'warning', refusal_threshold: 1 },
            { level_key: 'parent_contact', refusal_threshold: 2 },
            { level_key: 'detention', refusal_threshold: 3 },
            { level_key: 'iss', refusal_threshold: 4 },
            { level_key: 'admin_review', refusal_threshold: 5 }
          ];
          const sortedLevels = [...levels].sort((a, b) => b.refusal_threshold - a.refusal_threshold);
          for (const level of sortedLevels) {
            if (refusalCount >= level.refusal_threshold) {
              consequenceLevel = level.level_key;
              break;
            }
          }
        }

        const makeupOwed = colMap.makeupOwed !== -1 && cols[colMap.makeupOwed] ?
          parseInt(cols[colMap.makeupOwed]) :
          refusalCount;

        const makeupCompleted = colMap.makeupCompleted !== -1 && cols[colMap.makeupCompleted] ?
          parseInt(cols[colMap.makeupCompleted]) :
          0;

        // Auto-assign AP based on grade level
        let assignedAP = null;
        const grade = parseInt(gradeLevel);
        if (grade >= 4 && grade <= 5) {
          assignedAP = aps.find(ap => ap.ap_grade_cohort === '4-5');
        } else if (grade >= 6 && grade <= 8) {
          assignedAP = aps.find(ap => ap.ap_grade_cohort === '6-8');
        }

        // Check if record exists
        const existing = existingTracking.find(t => t.student_id === otherId);

        const recordData = {
          student_name: studentName,
          grade_level: gradeLevel,
          miss_count: refusalCount,
          current_consequence: consequenceLevel,
          makeup_days_owed: makeupOwed,
          makeup_days_completed: makeupCompleted,
          assigned_ap_email: assignedAP?.email || (existing?.assigned_ap_email || null),
          assigned_ap_name: assignedAP?.full_name || (existing?.assigned_ap_name || null),
          status: makeupCompleted >= makeupOwed ? 'resolved' : 'active'
        };

        if (existing) {
          recordsToUpdate.push({ id: existing.id, data: recordData });
          if (assignedAP && existing.assigned_ap_email !== assignedAP.email) {
            apAssigned++;
          }
        } else {
          recordsToCreate.push({
            student_id: otherId,
            ...recordData,
            makeup_dates: JSON.stringify([]),
            scheduled_makeup_dates: JSON.stringify([]),
            miss_history: JSON.stringify([])
          });
          if (assignedAP) {
            apAssigned++;
          }
        }
      }

      // Bulk create new records
      if (recordsToCreate.length > 0) {
        setProgress(`Creating ${recordsToCreate.length} new consequence records...`);
        await base44.entities.EAMissTracking.bulkCreate(recordsToCreate);
      }

      // Update existing records (batch in groups of 20)
      if (recordsToUpdate.length > 0) {
        setProgress(`Updating ${recordsToUpdate.length} existing records...`);
        for (let i = 0; i < recordsToUpdate.length; i += 20) {
          const batch = recordsToUpdate.slice(i, i + 20);
          await Promise.all(batch.map(r => base44.entities.EAMissTracking.update(r.id, r.data)));
        }
      }

      return {
        createdCount: recordsToCreate.length,
        updatedCount: recordsToUpdate.length,
        skippedCount,
        apAssigned
      };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(['eaMissTracking']);
      setMessage({
        type: 'success',
        text: `✅ Consequences: Created ${data.createdCount} new records, updated ${data.updatedCount} existing. ${data.apAssigned} students auto-assigned to APs.${data.skippedCount > 0 ? ` Skipped ${data.skippedCount} incomplete records.` : ''}`
      });
      setConsequenceData("");
      setProcessing(false);
      setProgress("");
    },
    onError: (err) => {
      setMessage({ type: 'error', text: err.message || 'Failed to process consequence data' });
      setProcessing(false);
      setProgress("");
    }
  });

  const handleProcessEA = () => {
    setProcessing(true);
    setMessage(null);
    processEARosterMutation.mutate();
  };

  const handleProcessASD = () => {
    setProcessing(true);
    setMessage(null);
    processASDRosterMutation.mutate();
  };

  const handleProcessConsequences = () => {
    setProcessing(true);
    setMessage(null);
    processConsequencesMutation.mutate();
  };

  return (
    <div className="p-4 md:p-8 bg-gradient-to-br from-slate-50 to-blue-50 min-h-screen">
      <div className="max-w-6xl mx-auto space-y-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center">
            <Database className="w-6 h-6 text-indigo-600" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Import Historical Data</h1>
            <p className="text-slate-600 mt-1">Import EA/ASD Rosters and Consequence Tracking to Start New Cycle</p>
          </div>
        </div>

        {message && (
          <Alert className={message.type === 'success' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}>
            {message.type === 'success' ? (
              <CheckCircle className="w-4 h-4 text-green-600" />
            ) : (
              <AlertCircle className="w-4 h-4 text-red-600" />
            )}
            <AlertDescription className={message.type === 'success' ? 'text-green-900' : 'text-red-900'}>
              {message.text}
            </AlertDescription>
          </Alert>
        )}

        {progress && processing && (
          <Alert className="bg-blue-50 border-blue-200">
            <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
            <AlertDescription className="text-blue-900">{progress}</AlertDescription>
          </Alert>
        )}

        <Alert className="bg-blue-50 border-blue-200">
          <AlertCircle className="w-4 h-4 text-blue-700" />
          <AlertDescription className="text-blue-900">
            <strong>📋 Workflow:</strong>
            <ol className="list-decimal list-inside mt-2 space-y-1 ml-2">
              <li>Import <strong>EA Roster</strong> (current EA assignments)</li>
              <li>Import <strong>ASD Roster</strong> (students in after-school detention)</li>
              <li>Import <strong>Consequence Tracking</strong> (refusal counts, consequence levels, makeup days)</li>
              <li>Then use <strong>EA Attendance</strong> and <strong>ASD Attendance</strong> pages to mark today's PEAR</li>
            </ol>
          </AlertDescription>
        </Alert>

        <Tabs defaultValue="ea" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="ea">EA Roster</TabsTrigger>
            <TabsTrigger value="asd">ASD Roster</TabsTrigger>
            <TabsTrigger value="consequences">Consequence Tracking</TabsTrigger>
          </TabsList>

          {/* EA ROSTER */}
          <TabsContent value="ea">
            <Card className="border-none shadow-lg">
              <CardHeader>
                <CardTitle>Import EA Roster</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Alert className="bg-yellow-50 border-yellow-200">
                  <AlertCircle className="w-4 h-4 text-yellow-700" />
                  <AlertDescription className="text-yellow-900 text-sm">
                    <strong>Format:</strong> Paste from Excel/CSV with same columns as failure report:
                    <br />• <strong>Required:</strong> Other ID, Student Last Name, Student First Name, Teacher, Grade Level
                    <br />• <strong>Recommended:</strong> Teacher Email (for teacher access), Course Short Description
                    <br />• All other columns from failure report are supported
                    <br />• Students will be marked as "Pending" - use EA Attendance page to mark PEAR
                  </AlertDescription>
                </Alert>

                <div className="space-y-2">
                  <Label>Paste EA Roster Data</Label>
                  <Textarea
                    value={eaRosterData}
                    onChange={(e) => setEaRosterData(e.target.value)}
                    placeholder="Paste complete EA roster from Excel (include header row)..."
                    className="h-80 font-mono text-xs"
                    disabled={processing}
                  />
                </div>

                <Button
                  onClick={handleProcessEA}
                  disabled={!eaRosterData.trim() || processing}
                  className="bg-red-700 hover:bg-red-800 w-full"
                >
                  {processing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Import EA Roster
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ASD ROSTER */}
          <TabsContent value="asd">
            <Card className="border-none shadow-lg">
              <CardHeader>
                <CardTitle>Import ASD Roster</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Alert className="bg-yellow-50 border-yellow-200">
                  <AlertCircle className="w-4 h-4 text-yellow-700" />
                  <AlertDescription className="text-yellow-900 text-sm">
                    <strong>Format:</strong> Same as EA roster - paste from Excel/CSV
                    <br />• <strong>Required:</strong> Other ID, Student Last Name, Student First Name, Teacher, Grade Level
                    <br />• These students will be marked as <strong>ASD</strong> (After School Detention)
                    <br />• Students will show up on ASD Roster and ASD Attendance pages
                  </AlertDescription>
                </Alert>

                <div className="space-y-2">
                  <Label>Paste ASD Roster Data</Label>
                  <Textarea
                    value={asdRosterData}
                    onChange={(e) => setAsdRosterData(e.target.value)}
                    placeholder="Paste complete ASD roster from Excel (include header row)..."
                    className="h-80 font-mono text-xs"
                    disabled={processing}
                  />
                </div>

                <Button
                  onClick={handleProcessASD}
                  disabled={!asdRosterData.trim() || processing}
                  className="bg-red-700 hover:bg-red-800 w-full"
                >
                  {processing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Import ASD Roster
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* CONSEQUENCE TRACKING */}
          <TabsContent value="consequences">
            <Card className="border-none shadow-lg">
              <CardHeader>
                <CardTitle>Import Consequence Tracking</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Alert className="bg-yellow-50 border-yellow-200">
                  <AlertCircle className="w-4 h-4 text-yellow-700" />
                  <AlertDescription className="text-yellow-900 text-sm">
                    <strong>Format:</strong> Create a simple spreadsheet with these columns:
                    <br />• <strong>Other ID</strong> (or Student ID) - REQUIRED
                    <br />• <strong>Student Name</strong> - Optional (will auto-fill from roster if missing)
                    <br />• <strong>Grade Level</strong> - Optional (will auto-fill from roster if missing)
                    <br />• <strong>Refusal Count</strong> (or Refusal Days) - REQUIRED (e.g., 2, 3, 5)
                    <br />• <strong>Consequence Level</strong> - Optional (e.g., warning, parent_contact, detention, iss, admin_review)
                    <br />• <strong>Makeup Days Owed</strong> - Optional (defaults to refusal count)
                    <br />• <strong>Makeup Days Completed</strong> - Optional (defaults to 0)
                    <br /><br />
                    <strong>💡 Tip:</strong> If Consequence Level is blank, system will auto-calculate based on refusal count. APs will be auto-assigned by grade level.
                  </AlertDescription>
                </Alert>

                <div className="space-y-2">
                  <Label>Paste Consequence Data</Label>
                  <Textarea
                    value={consequenceData}
                    onChange={(e) => setConsequenceData(e.target.value)}
                    placeholder={`Paste consequence data from Excel (include header row)...\nExample:\nOther ID\tStudent Name\tGrade Level\tRefusal Count\tConsequence Level\tMakeup Days Owed\tMakeup Days Completed\n12345\tJohn Doe\t7\t3\tdetention\t3\t1\n67890\tJane Smith\t5\t2\tparent_contact\t2\t0`}
                    className="h-80 font-mono text-xs"
                    disabled={processing}
                  />
                </div>

                <Button
                  onClick={handleProcessConsequences}
                  disabled={!consequenceData.trim() || processing}
                  className="bg-red-700 hover:bg-red-800 w-full"
                >
                  {processing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Import Consequence Tracking
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <Card className="border-none shadow-lg bg-slate-50">
          <CardContent className="p-6">
            <h3 className="font-semibold text-slate-900 mb-3">📚 Next Steps After Import:</h3>
            <ol className="space-y-2 text-sm text-slate-700 list-decimal list-inside">
              <li>Go to <strong>EA Roster</strong> page and verify students are showing</li>
              <li>Go to <strong>ASD Roster</strong> page and verify ASD students are showing</li>
              <li>Go to <strong>EA Attendance</strong> page to mark today's PEAR (Present/Excused/Absent/Refusal)</li>
              <li>Go to <strong>ASD Attendance</strong> page to mark ASD attendance</li>
              <li>Go to <strong>EA Consequences</strong> (Admin) or <strong>EA Consequences</strong> (AP) to view/manage consequence tracking</li>
              <li><strong>Run Closeout</strong> at end of day to finalize attendance and move refusals to ASD</li>
            </ol>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
